import React from 'react';
import { useTheme } from './ThemeContext';
import { CheckCircle, XCircle, X } from 'lucide-react';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'success' | 'error';
  title: string;
  message: string;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message
}) => {
  const { theme } = useTheme();

  if (!isOpen) return null;

  const isSuccess = type === 'success';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className={`${theme === 'dark' ? 'bg-theme-primary border-theme-tertiary' : 'bg-white border-gray-200'} rounded-lg shadow-2xl max-w-md w-full border overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              {isSuccess ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
            </div>
            <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-theme-secondary ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-500'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <p className={`text-sm ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-600'} mb-6`}>
            {message}
          </p>

          {/* Action */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isSuccess
                  ? 'bg-green-600 hover:bg-green-700 text-theme-primary'
                  : 'bg-red-600 hover:bg-red-700 text-theme-primary'
              }`}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;